import { useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import {
  type EventStatus,
  getEventStatusDisplay,
  getEventStatusToneClass,
  normalizeEventStatus,
} from "@app/lib/event-status";
import {
  GET_ADMIN_EVENT_ROLLUPS,
  LIST_ADMIN_EVENTS_PAGE_INDEX,
} from "@app/graphql/events.queries";
import { ClockIcon, FilterIcon, SearchIcon } from "@app/lib/events-admin-icons";
import { escapeCsvValue, formatShortDate } from "@app/lib/events-admin-utils";
import { DonutChart } from "@app/components/donut-chart";
import { formatOneworld } from "@app/components/oneworld-word";
import { SkeletonTableRow } from "@app/components/skeleton";
import { useOrganizationId } from "@app/hooks/use-organization-id";
import { useServerEventsPage } from "@app/hooks/use-server-events-page";
import { useNavigate } from "react-router";
import {
  Button,
  PaginationPageDefault,
} from "@its-engineering-and-development/ui-foundation/components";
import "./admin-dashboard-page.css";

type ApiEvent = {
  eventId: string;
  eventName: string;
  destination: string;
  organizerEmail: string | null;
  startDate: string;
  endDate: string;
  status: string;
  updatedAt: string;
  eventType: string;
};

type NormalizedEvent = {
  id: string;
  name: string;
  destination: string;
  organizerEmail: string | null;
  startDate: string;
  endDate: string;
  status: string;
  updatedAt: string;
  eventType: string;
};

type AdminEventRollups = {
  review: number;
  active: number;
  total: number;
  byStatus: unknown;
};

type AdminEventRollupsQueryData = {
  getAdminEventRollups: AdminEventRollups;
};

type AdminEventRollupsQueryVariables = {
  organizationId: string;
};

const EVENT_TYPE_ONE_WORLD = "ONE_WORLD";
const EVENT_TYPE_ONEWORLD_DISPLAY = "ONEWORLD";
const PAGE_SIZE = 10;
const MINUTE_IN_MILLISECONDS = 60_000;
const HOUR_IN_MILLISECONDS = 60 * MINUTE_IN_MILLISECONDS;
const DAY_IN_MILLISECONDS = 24 * HOUR_IN_MILLISECONDS;

type AdminStatusFilter =
  | "IN_REVIEW"
  | "REGISTRATION_OPEN"
  | "COMPLETED"
  | "CANCELLED"
  | "DENIED";

function parseStatusCounts(
  value: AdminEventRollups["byStatus"] | undefined,
): Partial<Record<EventStatus, unknown>> {
  if (!value) return {};

  if (typeof value === "object") {
    return Array.isArray(value)
      ? {}
      : (value as Partial<Record<EventStatus, unknown>>);
  }

  if (typeof value !== "string") return {};

  try {
    const parsed: unknown = JSON.parse(value);
    return typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? (parsed as Partial<Record<EventStatus, unknown>>)
      : {};
  } catch {
    return {};
  }
}

function getStatusCount(
  counts: Partial<Record<EventStatus, unknown>>,
  status: EventStatus,
): number {
  const value = counts[status];
  return typeof value === "number" ? value : 0;
}

function normalizeEvent(eventItem: ApiEvent): NormalizedEvent {
  const normalizedStatus = normalizeEventStatus(eventItem.status);

  return {
    id: eventItem.eventId,
    name: eventItem.eventName,
    destination: eventItem.destination,
    organizerEmail: eventItem.organizerEmail ?? null,
    startDate: eventItem.startDate,
    endDate: eventItem.endDate,
    status: normalizedStatus ?? eventItem.status,
    updatedAt: eventItem.updatedAt,
    eventType: eventItem.eventType,
  };
}

function getElapsedTime(updatedAt: string, currentTime: number): string | null {
  const updatedAtTime = Date.parse(updatedAt);
  if (Number.isNaN(updatedAtTime)) return null;

  const elapsedMilliseconds = Math.max(0, currentTime - updatedAtTime);

  if (elapsedMilliseconds >= DAY_IN_MILLISECONDS) {
    return `${Math.floor(elapsedMilliseconds / DAY_IN_MILLISECONDS)}d`;
  }

  if (elapsedMilliseconds >= HOUR_IN_MILLISECONDS) {
    return `${Math.floor(elapsedMilliseconds / HOUR_IN_MILLISECONDS)}h`;
  }

  return `${Math.floor(elapsedMilliseconds / MINUTE_IN_MILLISECONDS)}m`;
}

function getEventTypeDisplay(eventType: string): string {
  const normalizedEventType = eventType.trim().toUpperCase();

  if (normalizedEventType === EVENT_TYPE_ONE_WORLD) {
    return EVENT_TYPE_ONEWORLD_DISPLAY;
  }

  return normalizedEventType;
}

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const tableSurfaceRef = useRef<HTMLElement>(null);
  const areToolbarControlsHidden = true;
  const hiddenToolbarControlStyle = areToolbarControlsHidden
    ? { display: "none" }
    : undefined;
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] =
    useState<AdminStatusFilter | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const { data: rollupData, loading: isLoadingRollups } = useQuery<
    AdminEventRollupsQueryData,
    AdminEventRollupsQueryVariables
  >(GET_ADMIN_EVENT_ROLLUPS, {
    variables: { organizationId: organizationId ?? "" },
    skip: !organizationId,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;

    const scheduleNextUpdate = () => {
      const millisecondsUntilNextMinute =
        MINUTE_IN_MILLISECONDS - (Date.now() % MINUTE_IN_MILLISECONDS);

      timerId = setTimeout(() => {
        setCurrentTime(Date.now());
        scheduleNextUpdate();
      }, millisecondsUntilNextMinute);
    };

    scheduleNextUpdate();

    return () => clearTimeout(timerId);
  }, []);

  const {
    items: events,
    page,
    totalPages,
    isLoading,
    error,
    retry,
    setPage,
  } = useServerEventsPage<ApiEvent, NormalizedEvent>({
    query: LIST_ADMIN_EVENTS_PAGE_INDEX,
    organizationId,
    pageSize: PAGE_SIZE,
    search: searchQuery,
    variables: selectedStatus ? { state: selectedStatus } : undefined,
    mapItem: normalizeEvent,
  });

  const showSkeleton = events.length === 0 && isLoading;
  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.requestAnimationFrame(() => {
      const tableSurface = tableSurfaceRef.current;
      if (!tableSurface) return;

      const shellHeight =
        document.querySelector<HTMLElement>(".events-shell")?.offsetHeight ?? 0;
      window.scrollTo({
        top: Math.max(
          0,
          window.scrollY +
            tableSurface.getBoundingClientRect().top -
            shellHeight -
            16,
        ),
        behavior: "smooth",
      });
    });
  };

  return (
    <section className="events-page events-page--stack admin-dashboard-page">
      <header className="events-page__hero">
        <div>
          <h1 className="events-page__title events-page__title--admin">
            {formatOneworld("oneworld Event Management")}
          </h1>
          <p className="events-page__subtitle">
            {formatOneworld(
              "View and manage travel events across the oneworld network.",
            )}
          </p>
        </div>
      </header>

      <AdminEventRollupCards
        rollups={rollupData?.getAdminEventRollups}
        isLoading={isLoadingRollups}
        selectedStatus={selectedStatus}
        onSelectStatus={(status) =>
          setSelectedStatus((currentStatus) =>
            currentStatus === status ? null : status,
          )
        }
      />

      <div className="events-admin-toolbar">
        <div className="events-admin-toolbar__search">
          <SearchIcon className="size-4" />
          <input
            type="text"
            placeholder="Search Event Name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search events"
          />
        </div>
        <Button
          size="sm"
          color="secondary"
          className="events-outline-button"
          onPress={
            areToolbarControlsHidden
              ? undefined
              : () => setShowFilters((prev) => !prev)
          }
          style={hiddenToolbarControlStyle}
        >
          <FilterIcon className="size-4" />
          {showFilters ? "Hide Filters" : "Filters"}
        </Button>
        <div className="events-admin-toolbar__spacer" />
        <Button
          size="sm"
          color="secondary"
          className="events-outline-button"
          onPress={
            areToolbarControlsHidden
              ? undefined
              : () => {
                  const csvRows = [
                    [
                      "Event ID",
                      "Event Name",
                      "Event Type",
                      "Destination",
                      "Departure",
                      "Return",
                      "Status",
                    ].join(","),
                    ...events.map((eventItem) =>
                      [
                        escapeCsvValue(eventItem.id),
                        escapeCsvValue(eventItem.name),
                        escapeCsvValue(
                          getEventTypeDisplay(eventItem.eventType),
                        ),
                        escapeCsvValue(eventItem.destination),
                        escapeCsvValue(eventItem.startDate),
                        escapeCsvValue(eventItem.endDate),
                        escapeCsvValue(
                          getEventStatusDisplay(eventItem.status).label,
                        ),
                      ].join(","),
                    ),
                  ];
                  const blob = new Blob([csvRows.join("\n")], {
                    type: "text/csv",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "admin-events-export.csv";
                  a.click();
                  URL.revokeObjectURL(url);
                }
          }
          style={hiddenToolbarControlStyle}
        >
          Export Table
        </Button>
      </div>

      <article
        ref={tableSurfaceRef}
        className="events-surface events-surface--table"
      >
        <table className="events-table events-table--admin">
          <thead>
            <tr>
              <th>Event ID</th>
              <th>Event Name</th>
              <th>Event Type</th>
              <th>Destination</th>
              <th>Organizer Email</th>
              <th>Departure</th>
              <th>Return</th>
              <th>Status</th>
              <th aria-label="Status age" />
            </tr>
          </thead>
          <tbody className="events-stagger">
            {showSkeleton ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <SkeletonTableRow key={`skeleton-${idx}`} columns={9} />
              ))
            ) : (
              <>
                {error ? (
                  <tr>
                    <td colSpan={9} className="events-table__empty">
                      <div role="alert">
                        {events.length > 0
                          ? "Unable to refresh events. Showing the latest available results."
                          : "Unable to load events. Check your connection and try again."}
                      </div>
                      <Button
                        size="sm"
                        color="secondary"
                        className="events-outline-button"
                        onPress={retry}
                      >
                        Try again
                      </Button>
                    </td>
                  </tr>
                ) : null}
                {events.length === 0 && !error ? (
                  <tr>
                    <td colSpan={9} className="events-table__empty">
                      {searchQuery.trim()
                        ? `No events match "${searchQuery.trim()}".`
                        : selectedStatus
                          ? `No ${getEventStatusDisplay(selectedStatus).label.toLowerCase()} events found in this organization.`
                          : "No events found in this organization."}
                    </td>
                  </tr>
                ) : (
                  events.map((eventItem) => (
                    <DashboardEventRow
                      key={eventItem.id}
                      eventItem={eventItem}
                      currentTime={currentTime}
                      onNavigate={() =>
                        navigate(`/admin/groups/${eventItem.id}`)
                      }
                    />
                  ))
                )}
              </>
            )}
          </tbody>
        </table>
        {!showSkeleton && totalPages > 1 ? (
          <PaginationPageDefault
            page={page}
            total={totalPages}
            onPageChange={handlePageChange}
            className="admin-dashboard-page__pagination"
          />
        ) : null}
      </article>
    </section>
  );
}

function AdminEventRollupCards({
  rollups,
  isLoading,
  selectedStatus,
  onSelectStatus,
}: {
  rollups: AdminEventRollups | undefined;
  isLoading: boolean;
  selectedStatus: AdminStatusFilter | null;
  onSelectStatus: (status: AdminStatusFilter) => void;
}) {
  if (!rollups && !isLoading) return null;

  const statusCounts = parseStatusCounts(rollups?.byStatus);
  const cards = [
    {
      label: "In Review",
      value: rollups?.review,
      color: "#21188f",
      filterState: "IN_REVIEW",
    },
    {
      label: "Active",
      value: rollups?.active,
      color: "#14a84b",
      // NOTE (ponytail): Active is aggregated, but filtering stays exact-state until the API supports the aggregate.
      filterState: "REGISTRATION_OPEN",
    },
    {
      label: "Completed",
      value: getStatusCount(statusCounts, "COMPLETED"),
      color: "#65728c",
      filterState: "COMPLETED",
    },
    {
      label: "Cancelled",
      value: getStatusCount(statusCounts, "CANCELLED"),
      color: "#c66a16",
      filterState: "CANCELLED",
    },
    {
      label: "Denied",
      value: getStatusCount(statusCounts, "DENIED"),
      color: "#c62f4b",
      filterState: "DENIED",
    },
  ] as const;
  const totalForDonut = Math.max(rollups?.total ?? 1, 1);

  return (
    <section
      className="events-admin-summary-grid events-stagger admin-dashboard-page__status-controls"
      aria-label="Event status summary"
      aria-busy={isLoading}
    >
      {cards.map((card) => (
        <button
          type="button"
          className={`events-admin-summary-card admin-dashboard-page__status-control${
            selectedStatus === card.filterState
              ? " admin-dashboard-page__status-control--selected"
              : ""
          }`}
          key={card.label}
          aria-label={`${card.label}: ${card.value ?? 0} events`}
          aria-pressed={selectedStatus === card.filterState}
          disabled={isLoading}
          onClick={() => onSelectStatus(card.filterState)}
        >
          <DonutChart
            value={card.value ?? 0}
            total={totalForDonut}
            color={card.color}
            showValue={!isLoading}
            label={
              isLoading
                ? `Loading ${card.label.toLowerCase()} events`
                : undefined
            }
          />
          <span className="events-admin-summary-card__label">{card.label}</span>
          <span className="events-admin-summary-card__action">
            {card.value ?? 0} {(card.value ?? 0) === 1 ? "Event" : "Events"}
          </span>
        </button>
      ))}
    </section>
  );
}

function DashboardEventRow({
  eventItem,
  currentTime,
  onNavigate,
}: {
  eventItem: NormalizedEvent;
  currentTime: number;
  onNavigate: () => void;
}) {
  const isInReview = eventItem.status === "IN_REVIEW";
  const statusAge = isInReview
    ? getElapsedTime(eventItem.updatedAt, currentTime)
    : null;

  return (
    <tr
      className="events-table__row--clickable"
      onClick={onNavigate}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onNavigate();
        }
      }}
    >
      <td data-label="Event ID">
        <span className="events-table__id" title={eventItem.id}>
          {eventItem.id}
        </span>
      </td>
      <td data-label="Event Name">
        <span className="events-table__primary-link" title={eventItem.name}>
          {eventItem.name}
        </span>
      </td>
      <td data-label="Event Type">
        {getEventTypeDisplay(eventItem.eventType)}
      </td>
      <td data-label="Destination">{eventItem.destination}</td>
      <td
        data-label="Organizer Email"
        {...(eventItem.organizerEmail
          ? { title: eventItem.organizerEmail }
          : {})}
      >
        {eventItem.organizerEmail ?? "—"}
      </td>
      <td data-label="Departure">
        <strong className="events-table__metric">
          {formatShortDate(eventItem.startDate)}
        </strong>
      </td>
      <td data-label="Return">
        <strong className="events-table__metric">
          {formatShortDate(eventItem.endDate)}
        </strong>
      </td>
      <td data-label="Status" className="admin-dashboard-page__status-cell">
        <span className={getEventStatusToneClass(eventItem.status)}>
          {getEventStatusDisplay(eventItem.status).label}
        </span>
      </td>
      <td
        data-label="Status Age"
        className="admin-dashboard-page__status-age-cell"
      >
        {statusAge ? (
          <span
            className="admin-dashboard-page__status-timeframe"
            aria-label={`Status age: ${statusAge}`}
          >
            <ClockIcon className="admin-dashboard-page__status-timeframe-icon" />
            {statusAge}
          </span>
        ) : null}
      </td>
    </tr>
  );
}
